import { DrawProduct, View } from "@novorender/api";
import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from "gl-matrix";
import { MutableRefObject, useCallback, useEffect, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { Canvas2D } from "components";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CameraState, drawPart, getCameraState } from "features/engine2D";
import { getSnapInfo2d, PointSnapInfo } from "features/render/measureSnap";

import { getMeasurePointsFromTracer, LaserPoint, selectOutlineLasers, TraceMeasurement } from "./outlineLaserSlice";

export function OutlineLaserCanvas({
    renderFnRef,
    svg,
}: {
    renderFnRef: MutableRefObject<((moved: boolean) => void) | undefined>;
    svg: SVGSVGElement | null;
}) {
    const {
        state: { size, view },
    } = useExplorerGlobals();
    const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null | undefined>(null);

    const outlineLasers = useAppSelector(selectOutlineLasers);

    const draw = useCallback(() => {
        if (!view || !ctx || !canvas || !svg) {
            return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const camera = getCameraState(view.renderState.camera);

        const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });

        const doDrawConnection = (
            measurement: TraceMeasurement | undefined,
            points: LaserPoint[],
            position: "start" | "end",
            color: string,
        ) => {
            if (!measurement) {
                return;
            }
            const [idx, point] =
                position === "start"
                    ? [measurement.startIdx, measurement.start]
                    : [measurement.endIdx, measurement.end];

            if (idx !== undefined) {
                const connection = points[idx].connection;
                if (connection) {
                    drawConnection(view, connection, toScreenSpace, color, ctx);
                    return;
                }
            }
            if (point) {
                drawConnection(view, { type: "vertex", connection: point }, toScreenSpace, color, ctx);
            }
        };

        outlineLasers.forEach((laser) => {
            const { left, right, up, down, zUp, zDown, measurementX: x, measurementY: y, measurementZ: z } = laser;
            const xPts = x && getMeasurePointsFromTracer(x, left, right);
            const yPts = y && getMeasurePointsFromTracer(y, down, up);
            const zPts = z && getMeasurePointsFromTracer(z, zDown, zUp);

            if (xPts) {
                const color = "#D92626";
                const drawProd = view.measure?.draw.getDrawObjectFromPoints(
                    xPts.map((p) => p.position),
                    {
                        closed: false,
                        angles: false,
                        generateLengthLabels: false,
                    },
                );
                if (drawProd) {
                    renderTrace({ ctx, camera, drawProd, color });
                }

                doDrawConnection(x, left, "start", color);
                doDrawConnection(x, right, "end", color);
            }

            if (yPts) {
                const color = "#E5FF00";
                const drawProd = view.measure?.draw.getDrawObjectFromPoints(
                    yPts.map((p) => p.position),
                    {
                        closed: false,
                        angles: false,
                        generateLengthLabels: false,
                    },
                );
                if (drawProd) {
                    renderTrace({ ctx, camera, drawProd, color });
                }

                doDrawConnection(y, down, "start", color);
                doDrawConnection(y, up, "end", color);
            }

            if (zPts) {
                const color = "#2066DF";
                const drawProd = view.measure?.draw.getDrawObjectFromPoints(
                    zPts.map((p) => p.position),
                    {
                        closed: false,
                        angles: false,
                        generateLengthLabels: false,
                    },
                );
                if (drawProd) {
                    renderTrace({ ctx, camera, drawProd, color });
                }

                doDrawConnection(z, zUp, "end", color);
            }
        });
    }, [ctx, canvas, view, outlineLasers, svg]);

    useEffect(() => {
        draw();
    }, [draw, size, ctx, canvas]);

    useEffect(() => {
        renderFnRef.current = animate;
        return () => (renderFnRef.current = undefined);
        function animate(moved: boolean) {
            if (!view || !moved) {
                return;
            }
            draw();
        }
    }, [draw, renderFnRef, view]);

    return outlineLasers.length ? (
        <Canvas2D
            id="outline-laser-canvas"
            data-include-snapshot
            ref={(el) => {
                setCanvas(el);
                setCtx(el?.getContext("2d") ?? null);
            }}
            width={size.width}
            height={size.height}
        />
    ) : null;
}

const renderTrace = ({
    ctx,
    camera,
    drawProd,
    color,
}: {
    ctx: CanvasRenderingContext2D;
    camera: CameraState;
    drawProd: DrawProduct;
    color: string;
}) => {
    drawProd.objects.forEach((obj) =>
        obj.parts.forEach((part) =>
            drawPart(
                ctx,
                camera,
                part,
                {
                    lineColor: color,
                },
                2,
                {
                    type: "default",
                },
            ),
        ),
    );
};

function drawConnection(
    view: View,
    connection: PointSnapInfo,
    toScreenSpace: (p: ReadonlyVec3) => vec2 | undefined,
    color: string,
    ctx: CanvasRenderingContext2D,
) {
    const connection2d = getSnapInfo2d(view, toScreenSpace, connection);
    const point2d = connection2d?.connection;
    if (!point2d) {
        return;
    }

    const point = connection.connection;

    // Draw a line segment lying on the edge that laser goes into.
    if (connection2d?.type === "tangent") {
        const start2d = connection2d.start ?? connection2d.end;
        if (start2d) {
            const dist = 10;

            const distToStart = vec2.distance(start2d, point2d);
            vec2.lerp(start2d, point2d, start2d, dist / distToStart);
            const end2d = vec2.sub(vec2.create(), start2d, point2d);
            vec2.negate(end2d, end2d);
            vec2.add(end2d, point2d, end2d);

            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(start2d[0], start2d[1]);
            ctx.lineTo(end2d[0], end2d[1]);
            ctx.stroke();
            ctx.closePath();

            return;
        }
    }

    // Draw a circle on the face that laser goes into.
    // I don't think it's possible to draw 3d->2d projected circle using just ellipse or arc
    // so we will draw 4 bezier curves instead.
    if (connection?.type === "normal") {
        const r1 = 5;
        const r2 = 12;
        const anchorK = 0.552284749831;

        const baseUp = vec3.fromValues(0, 0, 1);
        if (Math.abs(vec3.dot(baseUp, connection.normal)) > 0.999) {
            baseUp[0] = 0;
            baseUp[1] = 1;
            baseUp[2] = 0;
        }

        const right = vec3.cross(vec3.create(), connection.normal, baseUp);
        vec3.normalize(right, right);
        vec3.scale(right, right, 0.01);
        const up = vec3.cross(vec3.create(), right, connection.normal);
        vec3.normalize(up, up);
        vec3.scale(up, up, 0.01);
        const down = vec3.negate(vec3.create(), up);
        const left = vec3.negate(vec3.create(), right);

        const upRight = vec3.scaleAndAdd(vec3.create(), up, right, anchorK);
        const rightUp = vec3.scaleAndAdd(vec3.create(), right, up, anchorK);
        const rightDown = vec3.scaleAndAdd(vec3.create(), right, down, anchorK);
        const downRight = vec3.scaleAndAdd(vec3.create(), down, right, anchorK);
        const downLeft = vec3.scaleAndAdd(vec3.create(), down, left, anchorK);
        const leftDown = vec3.scaleAndAdd(vec3.create(), left, down, anchorK);
        const leftUp = vec3.scaleAndAdd(vec3.create(), left, up, anchorK);
        const upLeft = vec3.scaleAndAdd(vec3.create(), up, left, anchorK);

        vec3.add(up, point, up);
        vec3.add(right, point, right);
        vec3.add(down, point, down);
        vec3.add(left, point, left);
        vec3.add(upRight, point, upRight);
        vec3.add(rightUp, point, rightUp);
        vec3.add(rightDown, point, rightDown);
        vec3.add(downRight, point, downRight);
        vec3.add(downLeft, point, downLeft);
        vec3.add(leftDown, point, leftDown);
        vec3.add(leftUp, point, leftUp);
        vec3.add(upLeft, point, upLeft);

        const up2d = toScreenSpace(up);
        const right2d = toScreenSpace(right);
        const down2d = toScreenSpace(down);
        const left2d = toScreenSpace(left);

        if (!(up2d && right2d && down2d && left2d)) {
            return;
        }

        const maxDist = Math.max(
            vec2.distance(up2d, point2d),
            vec2.distance(right2d, point2d),
            vec2.distance(down2d, point2d),
            vec2.distance(left2d, point2d),
        );

        const upRight2d = toScreenSpace(upRight);
        const rightUp2d = toScreenSpace(rightUp);
        const rightDown2d = toScreenSpace(rightDown);
        const downRight2d = toScreenSpace(downRight);
        const downLeft2d = toScreenSpace(downLeft);
        const leftDown2d = toScreenSpace(leftDown);
        const leftUp2d = toScreenSpace(leftUp);
        const upLeft2d = toScreenSpace(upLeft);
        if (
            !(upRight2d && rightUp2d && rightDown2d && downRight2d && downLeft2d && leftDown2d && leftUp2d && upLeft2d)
        ) {
            return;
        }

        const cp1Buf = vec2.create();
        const cp2Buf = vec2.create();
        const pBuf = vec2.create();
        const curveTo = (cp1: ReadonlyVec2, cp2: ReadonlyVec2, p: ReadonlyVec2, k: number) => {
            vec2.sub(cp1Buf, cp1, point2d);
            vec2.scaleAndAdd(cp1Buf, point2d, cp1Buf, k);
            vec2.sub(cp2Buf, cp2, point2d);
            vec2.scaleAndAdd(cp2Buf, point2d, cp2Buf, k);
            vec2.sub(pBuf, p, point2d);
            vec2.scaleAndAdd(pBuf, point2d, pBuf, k);
            ctx.bezierCurveTo(cp1Buf[0], cp1Buf[1], cp2Buf[0], cp2Buf[1], pBuf[0], pBuf[1]);
        };

        const k1 = r1 / maxDist;
        const start1 = vec2.sub(vec2.create(), up2d, point2d);
        vec2.scaleAndAdd(start1, point2d, start1, k1);

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(start1[0], start1[1]);
        curveTo(upRight2d, rightUp2d, right2d, k1);
        curveTo(rightDown2d, downRight2d, down2d, k1);
        curveTo(downLeft2d, leftDown2d, left2d, k1);
        curveTo(leftUp2d, upLeft2d, up2d, k1);
        ctx.fill();
        ctx.closePath();

        const k2 = r2 / maxDist;
        const start2 = vec2.sub(vec2.create(), up2d, point2d);
        vec2.scaleAndAdd(start2, point2d, start2, k2);

        ctx.fillStyle = color + "4c";
        ctx.beginPath();
        ctx.moveTo(start2[0], start2[1]);
        curveTo(upRight2d, rightUp2d, right2d, k2);
        curveTo(rightDown2d, downRight2d, down2d, k2);
        curveTo(downLeft2d, leftDown2d, left2d, k2);
        curveTo(leftUp2d, upLeft2d, up2d, k2);
        ctx.fill();
        ctx.closePath();

        return;
    }

    // Just a point otherwise
    const r = 3;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(point2d[0], point2d[1], r, 0, 2 * Math.PI);
    ctx.fill();
    ctx.closePath();
}
